import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { Address } from "types";
import { from } from "assets/libs/moment/src/lib/moment/from";
// import { InstallationStatusBadge } from "components/Installationstatus";
import { StatusBadge } from "components/Productlocationstatus";
import { getAuthCookie } from "../../utils/authCookies";
import {
    buildRemovalInstallerPayloadFields,
    resolveRemovalInstallerMembershipId,
    removalInstallerPickerFromOrder,
} from "api/removalApi";
const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    border: "1px solid #dee2e6",          // Light border
};


const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const SignageEditRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    // console.log("Removal Order ID:", id);
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [products, setProducts] = useState<any>(null);
    const [removalQuantities, setRemovalQuantities] = useState<Record<string, number>>({});
    const [installer, setInstaller] = useState<any[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);
    const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
    const [updatedProducts, setUpdatedProducts] = useState<any[]>([]);
    const [productNotes, setProductNotes] = useState<Record<string, { bin_location: string; note: string }>>({});
    const [updatedRotationalUnits, setUpdatedRotationalUnits] = useState<Record<string, {
        current_status: string;
        restock_quantity: number;
        writeoff_quantity: number;
        reason_to_writeoff: string;
        bin_location: string;
        note: string;
    }>>({});

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address.address_details ? address.address_details._id : address._id,
                label: address.label,
            }))
            : [];

    const priorityOptions = [
        { value: 1, label: "Critical" },
        { value: 2, label: "High Priority" },
        { value: 3, label: "Normal" },
    ];

    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Address | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
        useState<Address | null>(null);

    useEffect(() => {
        const fetchRemovalOrder = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    showError(data.error || "Failed to fetch removal order");
                    return;
                }

                setRemovalOrder(data);
                setUpdatedProducts(data.line_items || []);
                console.log("Fetched removal Order:", data);
                const initialQuantities: Record<string, number> = {};
                data.line_items?.forEach((item: any) => {
                    if (item.inventory_source !== "rotational") {
                        initialQuantities[item.product_id] = item.quantity_to_be_removed; // default quantity
                    }
                });
                setRemovalQuantities(initialQuantities);
            } catch (err) {
                // showError("Something went wrong while fetching removal order.");
                console.error(err);
            }
        };

        if (id) {
            fetchRemovalOrder();
        }
    }, [id]);

    useEffect(() => {
        if (removalOrder?.removal_details?.removal_address?.latitude && removalOrder?.removal_details?.removal_address?.longitude) {
            setSelectedLocation({
                lat: removalOrder.removal_details.removal_address.latitude,
                lng: removalOrder.removal_details.removal_address.longitude,
            });
        }
    }, [removalOrder]);

    //we are fetching signage team members for showing Assigned To dropdown
    useEffect(() => {
        const featchSignageTeammembers = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${userId}&licenseType=SIGNAGE_TEAM_MEMBER`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                console.log("featchSignageTeammembers:", data);
                if (!response.ok) {
                    throw new Error(data?.error || "Failed to fetch Removerse");
                }

                const installers = data?.map((user: any) => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    phone: user.phone1,
                }));

                setInstaller(installers); // 🟢 Now it's filtered and formatted
                const matchedInstaller = installers.find(
                    (option: any) => option.value === formik.values.installeId
                );
                if (matchedInstaller) {
                    setSelectedInstaller(matchedInstaller);
                    // console.log("selectedInstallar :", matchedInstaller)
                }
            } catch (err: any) {
                showError(err.message);
            } finally {
                setLoading(false);
            }
        };

        featchSignageTeammembers();
    }, [userId, removalOrder?.status]);

    // defult removal address set
    useEffect(() => {
        if (removalOrder?.billing_address) {
            setSelectedBillingAddress(removalOrder?.billing_address)
        }
        if (removalOrder?.agent_id) {
            fetchUserAddresses(removalOrder?.agent_id)
        }
        else if (removalOrder?.office) {
            fetchOfficeAddresses(removalOrder?.office._id)
            setSelectedOfficeBillingAddress(removalOrder?.billing_address)
        }
    }, [removalOrder]);

    const fetchUserAddresses = async (id: any) => {
        console.log("fetchUserAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/user-addresses/${id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("user addresses :", data);
            // setAddresses(data.addresses);
            setAddressObjects(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchOfficeAddresses = async (id: any) => {
        console.log("fetchOfficeAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/offices/${id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("office addresses :", data.officeAddress);
            setAddressObjects(data.officeAddress);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            order_number: removalOrder?.order_number || "",
            userType: removalOrder?.user_type,
            userFirstName: "",
            userLastName: "",
            userEmail: removalOrder?.user_email,
            userPhone: removalOrder?.user_phone,
            priority: removalOrder?.priority || 0,
            removal_date: removalOrder?.removal_details?.scheduled_date.split("T")[0] || "",
            scheduled_jobs: 0,
            restock_warehouse: "",
            installeId: resolveRemovalInstallerMembershipId(removalOrder),
            billing_address: removalOrder?.billing_address,
            // removal_address: removalOrder?.installation_details.removal_address,
            isSameAsBilling: false,
            agentId: removalOrder?.agent_id,
            officeId: removalOrder?.office?._id,
            billingAddressId: removalOrder?.billing_address?._id,
            removalAddressId: removalOrder?.installation_details?.removal_address?._id,
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            localLawsChecked: false,
            image: null,
            installation_charge: removalOrder?.removal_cost,
            discount: removalOrder?.discount_amount || 0,
            subtotal: removalOrder?.subtotal_amount,
            total: removalOrder?.total_amount,
            tax: 0,
        },
        validationSchema: Yup.object({
            priority: Yup.string()
                .required("Removal priority is required"),

            // ✅ Removal Date (Required)
            removal_date: Yup.string()
                .required("Removal date is required"),

            // ✅ Assigned To (Installer Required)
            installeId: Yup.string()
                .required("Installer is required"),

            // ✅ Billing Address (Required)
            billingAddressId: Yup.string()
                .required("Billing address is required"),
            // installeId: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // removal_address: Yup.string().required(
            //     "Removal address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
            // removal_date: Yup.string().required("Scheduled date is required"),
            // priority: Yup.number().required("Priority is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            removal_details: {
                                scheduled_date: new Date(values.removal_date).toISOString(),
                                removal_address: removalOrder?.installation_details?.removal_address,
                                ...buildRemovalInstallerPayloadFields(removalOrder, selectedInstaller, {
                                    contactNumber: selectedInstaller?.phone,
                                }),
                                estimated_duration: null,
                                actual_removal_date: null,
                                removal_notes: [
                                    {
                                        note: values.instructions || "",
                                        added_by: {
                                            id: userId,
                                            name: userName,
                                        },
                                        added_at: new Date().toISOString(),
                                    },
                                ],
                                tax_details: null,
                            },
                            billing_address: removalOrder?.agent_id ? selectedBillingAddress : selectedOfficeBillingAddress,
                            priority: Number(values.priority),
                            subtotal_amount: removalOrder?.subtotal_amount || 0,
                            tax_amount: 0,
                            total_amount: removalOrder?.total_amount || 0,
                            additional_instructions: [
                                {
                                    note: values.instructions || "",
                                    added_by: {
                                        id: userId,
                                        name: userName,
                                    },
                                    added_at: new Date().toISOString(),
                                },
                            ],
                        }),
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    showError(data.error || "Failed to update removal order");
                    throw new Error(data.error || "Failed to update removal order");
                }

                showSuccess(data.message || "Removal order updated successfully!");
                navigate(`/signage/jobs/removalWorkorder/${data._id}`);
            } catch (error: any) {
                console.error(error);
                showError(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        }

    });

    useEffect(() => {
        if (!removalOrder) return;
        const installerOption = removalInstallerPickerFromOrder(removalOrder);
        if (!installerOption?.value) return;
        if (formik.values.installeId !== installerOption.value) {
            formik.setFieldValue("installeId", installerOption.value, false);
        }
        setSelectedInstaller((prev) => prev ?? installerOption);
        setInstaller((prev) => {
            const list = prev ?? [];
            if (list.some((o) => o.value === installerOption.value)) return list;
            return [installerOption, ...list];
        });
    }, [removalOrder]);

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "categories");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    const isFieldEditable = (status: number, field: string): boolean => {
        const editableMap: Record<number, string[]> = {
            100: [
                // Draft
                "BillingAddress",
                "ScheduleDate",
                "Installer",
                "Priority",
                "Notes",
                "Instructions",
                "removalQuantity",
            ],
            200: [
                // Payment Accepted (Finalized)
                "ScheduleDate",
                "Installer",
                // "Priority",
                "Notes",
                "Instructions",
                "removalQuantity",
            ],
            300: [
                // Scheduled or Rescheduled
                "ScheduleDate",
                "Installer",
                "Priority",
                "Notes",
                "Instructions",
                "removalQuantity",
            ],
            // 400: [
            //     // In Progress
            //     "Instructions", // Only instructions editable
            // ],
            // 500 and 600: No edits allowed
        };

        const nonEditableStatuses = [400, 500, 600, 700, 800]; // Completed, Canceled

        if (nonEditableStatuses.includes(status)) return false;

        const editableFields = editableMap[status] || [];
        return editableFields.includes(field);
    };


    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content">
            <div className="page-container p-0">
                <h3 className="m-0 text-center fw-bold addheaders">Edit Removal Request </h3>
            </div>
            <div className="card">
                <div className="card-body">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row mt-3">
                            <div className="col-md-3 mb-3">
                                <label className="form-label table-cells">
                                    Removal Job Order
                                </label>
                                <input type="text" name="order_number" id="order_number" className="form-control" value={`${removalOrder?.order_number}`} placeholder="Enter Installation Job Order Number" disabled />
                                {/* {formik.touched.order_number && formik.errors.order_number && (
                                    <div className="text-danger">{formik.errors.order_number}</div>
                                )} */}
                            </div>

                            <div className="col-md-3 mb-3">
                                <label className="form-label table-cells">
                                    Removal Priority <span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={priorityOptions}
                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                    onChange={(option) =>
                                        formik.setFieldValue("priority", option?.value || "")
                                    }
                                    onBlur={formik.handleBlur}
                                    isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Priority")}
                                />
                                {formik.touched.priority && typeof formik.errors.priority === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.priority}</div>
                                )}
                            </div>
                            <div className="col-md-3 mb-3">
                                <label className="form-label table-cells">
                                    Removal Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="removal_date"
                                    name="removal_date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.removal_date}
                                    readOnly={!isFieldEditable(removalOrder?.status ?? 0, "ScheduleDate")}
                                />
                                {formik.touched.removal_date && typeof formik.errors.removal_date === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.removal_date}</div>
                                )}
                            </div>
                            <div className="col-md-3 mb-3">
                                <label className="form-label table-cells">
                                    Assigned To <span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="installeId"
                                    name="installeId"
                                    options={installer}
                                    value={
                                        installer?.find((option) => option.value === formik.values.installeId) ||
                                        removalInstallerPickerFromOrder(removalOrder) ||
                                        null
                                    }
                                    onChange={(selected) => {
                                        setSelectedInstaller(selected);
                                        formik.setFieldValue("installeId", selected?.value || "")
                                    }}
                                    onBlur={formik.handleBlur}
                                    isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Installer")}
                                />
                                {formik.touched.installeId && typeof formik.errors.installeId === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.installeId}</div>
                                )}

                            </div>
                        </div>
                        <div className="dashed-line my-2"></div>
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <h3 className="invoice-subheaders">Address details</h3>
                            </div>
                            <div className="col-sm-4">
                                <label
                                    htmlFor="billingAddressId"
                                    className="form-label table-cells"
                                >
                                    Billing Address <span className="text-danger">*</span>
                                </label>
                                <Select
                                    // className="form-control"
                                    id="billingAddress"
                                    name="billingAddress"
                                    options={addressOptions}
                                    value={
                                        addressOptions?.find(
                                            (option) =>
                                                option.value === formik.values.billingAddressId
                                        ) || null
                                    }
                                    onChange={(selected: any) => {
                                        console.log("Selected Billing Address:", selected); // Debugging log
                                        formik.setFieldValue("billingAddressId", selected?.value);
                                        const selectedAddress = addressObjects?.find(
                                            (addr) => (addr.address_details ? addr.address_details._id : addr._id) === selected?.value
                                        );
                                        if (formik.values.officeId) {
                                            setSelectedOfficeBillingAddress(selectedAddress || null);
                                        }
                                        else {
                                            setSelectedBillingAddress(selectedAddress?.address_details || null);
                                        }
                                    }}
                                    isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "BillingAddress")}
                                />
                                {formik.touched.billingAddressId && typeof formik.errors.billingAddressId === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                )}
                            </div>
                            <div className="col-sm-4">
                                <label
                                    htmlFor="removalAddressId"
                                    className="form-label table-cells"
                                >
                                    Removal Address
                                </label>
                                <input type="text" className="form-control" value={`${removalOrder?.removal_details?.removal_address?.addressLine1}, ${removalOrder?.removal_details?.removal_address?.city}, ${removalOrder?.removal_details?.removal_address?.state}`} disabled />
                            </div>
                        </div>
                        <div className="dashed-line my-2"></div>
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <h3 className="text-center mb-4 table-cells">Map View</h3>
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                    // onClick={handleMapClick}
                                    >
                                        {selectedLocation && <Marker position={selectedLocation} />}
                                    </GoogleMap>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <h4 className="invoice-subheaders mb-3">Product details </h4>

                                <div className="row mb-2">
                                    <div className="col-md-12" id="row-overlap">
                                        <table className="table ">
                                            <thead className="bg-primary-subtle">
                                                <tr>
                                                    <th className="text-center table-headers" style={{ width: "15%" }}>Product Name</th>
                                                    <th className="text-center table-headers" style={{ width: "10%" }}>SKU</th>
                                                    <th className="text-center table-headers" style={{ width: "3%" }}>Qty </th>
                                                    <th className="text-center table-headers" style={{ width: "5%" }}>Qty to remove </th>
                                                    <th className="text-center table-headers" style={{ width: "15%" }}>Product Location Status</th>
                                                    {/* <th className="text-center">Action</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {updatedProducts?.filter((item: any) => item.inventory_source !== "rotational")?.map((item: any, index: number) => (
                                                    <tr key={index}>
                                                        <td className="text-center align-middle table-cells text-ellipsis">{item.product_name}</td>
                                                        <td className="text-center align-middle table-cells">{item.sku}</td>
                                                        <td className="text-center align-middle table-cells" >{item.available_quantity}</td>
                                                        <td className="text-center align-middle table-cells">
                                                            <input
                                                                type="number"
                                                                className="form-control text-center align-middle table-cells"
                                                                value={removalQuantities[item.product_id] || 0}
                                                                // min={1}
                                                                onChange={(e) => {
                                                                    const inputVal = parseInt(e.target.value) || 1;
                                                                    const clampedVal = Math.min(inputVal, item.quantity_to_be_removed); // ✅ cap at max available

                                                                    setRemovalQuantities((prev) => ({
                                                                        ...prev,
                                                                        [item.product_id]: clampedVal,
                                                                    }));
                                                                }}
                                                                readOnly={!isFieldEditable(removalOrder?.status ?? 0, "removalQuantity")}
                                                            />
                                                        </td>
                                                        <td className="text-center align-middle table-cells">
                                                            <span className="">
                                                                <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                            </span>
                                                        </td>

                                                        {/* <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td> */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                </div>

                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="table-cells" htmlFor="">Additional Instructions </label>
                                    <textarea
                                        name="instructions"
                                        id="instructions"
                                        className="form-control"
                                        value={formik.values.instructions}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        rows={4}
                                        readOnly={!isFieldEditable(removalOrder?.status ?? 0, "Instructions")}
                                    ></textarea>
                                    <div className="col-md-4 mt-3">
                                        <label className="fw-semibold mb-1" htmlFor="localLaws">
                                            Local Laws Compliance:
                                        </label>
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                id="localLaws"
                                                name="localLawsChecked"
                                                className="form-check-input"
                                                checked={formik.values.localLawsChecked}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <label htmlFor="localLaws" className="form-check-label ms-2">
                                                I acknowledge adherence to local laws
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* <div className="row mb-3">
                                    <div className="col-md-8">
                                        <label htmlFor="">Upload Files</label>
                                        <input type="file" className="form-control" id="image" name="image" multiple />
                                    </div>
                                    <div className="col-md-4 pt-4">
                                        <button className="btn btn-success">Upload</button>
                                    </div>
                                </div> */}
                            </div>
                            <div className="col-md-6 d-flex align-items-center justify-content-end">
                                <div className="col-md-6 text-end">
                                    <table
                                        className=" mb-3"
                                        style={{
                                            border: "1px solid #dee2e6",
                                            borderRadius: "6px",
                                            padding: "10px",
                                        }}
                                    >
                                        <tbody>
                                            <tr>
                                                <td className="py-2 ps-3 fw-medium text-center table-cells">Removal Fee</td>
                                                <td className="py-2 pe-4 table-cells " style={{ paddingRight: "20px" }}>
                                                    ${Number(formik.values.installation_charge).toFixed(2)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 ps-3 fw-medium text-center table-cells">Subtotal</td>
                                                <td className="py-2 pe-4 table-cells" style={{ paddingRight: "20px" }}>
                                                    ${Number(formik.values.subtotal).toFixed(2)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 ps-3 fw-medium text-center table-cells">Tax</td>
                                                <td className="py-2 pe-4 table-cells" style={{ paddingRight: "20px" }}>
                                                    ${Number(formik.values.tax).toFixed(2)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 ps-3 fw-medium text-center table-cells">Total</td>
                                                <td className="py-2 pe-4 table-cells" style={{ paddingRight: "20px" }}>
                                                    ${Number(formik.values.total).toFixed(2)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {/* <div className="col-md-6">
                                <table className="calculation-table mb-3">
                                    <tr>
                                        <th>Removal Fee</th>
                                        <th>${Number(formik.values.installation_charge).toFixed(2)}</th>
                                    </tr>
                                    <tr>
                                        <th>Subtotal</th>
                                        <th>${Number(formik.values.subtotal).toFixed(2)}</th>
                                    </tr>
                                    <tr>
                                        <th>Tax</th>
                                        <td>${Number(formik.values.tax).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <th>Total</th>
                                        <td>${Number(formik.values.total).toFixed(2)}</td>
                                    </tr>
                                </table> */}
                            {/* <div className="mb-3">
                                    <label htmlFor="">Payment Details</label>
                                    <Select
                                        id="installer"
                                        name="installer"
                                        placeholder="Select Payment Method"
                                        options={[
                                            { value: "Saved Card", label: "Saved Card" },
                                            { value: "Add New Payment Method", label: "Add New Payment Method" },
                                        ]}
                                        onChange={(option) =>
                                            formik.setFieldValue("installer", option?.value || "")
                                        }
                                        onBlur={formik.handleBlur}
                                    />
                                </div> */}
                            {/* </div> */}
                        </div>
                        <div className="row mb-3 rcust-margin-4">
                            <div className="col-md-12 d-flex justify-content-between">
                                <button
                                    className="btn btn-outline-secondary br-8 backbutton mt-lg-auto"
                                    type="button"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-info mt-4 float-end"
                                    disabled={formSubmitting || !formik.values.localLawsChecked}
                                >
                                    Submit Request
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div >
        </div >
    );
};

export default SignageEditRemovalRequest;
